<template>
  <CalendarTemplate
    :events="events"
    :variables.sync="filters"
    :loadings="{ eventLoading, initEventLoad }"
    v-bind="{ showNoncompetitions }"
    :query-variables="queryVariables"
    @loadMore="loadMore"
    @attachLinksNewTarget="() => attachBlankToLinks()"
    @disableQuery="disableQuery"
  >
    <template #event-export>
      <AppExportFile
        v-if="$auth.canAccessRoute('EventCreationPage')"
        :prepare-function="getEvents"
        use-tooltip
      >
        {{
          showNoncompetitions
            ? $t("events:button.downloadNonCompData", "Download")
            : $t("common:button.downloadCsv", "Download as csv")
        }}
        <template #append:items="{ loading, setLoading }">
          <CalendarAdminExport
            v-if="$auth.canAccessRoute('CalendarAdminView')"
            v-bind="{ loading, setLoading }"
            :filters="queryVariables"
          />
        </template>
      </AppExportFile>
    </template>
    <template #load-more-btn>
      <AppLoadMoreBtn
        :has-more-results="hasMoreResults"
        :loading="eventLoading"
        @loadMore="loadMore"
      />
    </template>
  </CalendarTemplate>
</template>

<script>
import { provide, inject, ref, watch, computed } from "vue";
import dayjs from "@/plugins/dayjs";
import gql from "fraql";
import { useDisciplineHelper } from "@/composables/masterdata/competitions/disciplines";
import { useRegionHelper } from "@composables/masterdata/regions";
import { findURLParamsExternal } from "@elements/AppFilterNavigation.vue";
import { getFederationConfiguration } from "@composables/masterdata/config";
import { useEventCategoryHelper } from "@composables/masterdata/eventCategories";
import { useCompetitionLevelHelper } from "@composables/masterdata/competitions/competitionLevels";
import { useRouteScroll } from "@composables/route_scroll";
import { exportHandleArrayBooleanResponses } from "@composables/export_file";
import CalendarTemplate from "./template.vue";
import AppExportFile from "@elements/AppExportFile.vue";
import AppLoadMoreBtn from "@elements/AppLoadMoreBtn.vue";
import useReturnRoute from "@composables/return_route.ts";
import { eventsQueryVarsArgs } from "./eventsQueryVarArgs";
import CalendarAdminExport from "./CalendarAdmin/CalendarAdminExport.vue";

export default {
  name: "CalendarPage",
  components: {
    CalendarTemplate,
    AppExportFile,
    AppLoadMoreBtn,
    CalendarAdminExport
  },
  mixins: [useReturnRoute],
  props: {
    filterParams: {
      type: Object,
      default: null
    },
    showNoncompetitions: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const embeddedMode = inject("embeddedMode", false);
    const { scrollIntoElement } = useRouteScroll();
    const { masterEventCategoriesActive } = useEventCategoryHelper();
    const { getDisciplineCodenamesMapToId } = useDisciplineHelper();
    const { getRegionNameShortMapToId } = useRegionHelper();

    const highlightOfficialId = ref(null);
    provide(
      "highlightOfficialId",
      computed({
        get() {
          return highlightOfficialId.value;
        },
        set(v) {
          highlightOfficialId.value = v;
        }
      })
    );

    /**
     * Block this component's events query as Month and Admin views make their own
     * queries with same filters
     */
    const disableEventsQuery = ref(false);

    const filters = ref({
      eventsDatePeriod: null,
      profQualificationIds: [],
      eventCategoryIds: [],
      disciplinesSelected: [],
      competitionLevelsSelected: [],
      regionsSelected: [],
      seasonFilterSelected: null,
      eventPlaceFilterSelected: null,
      organisersSelected: null,
      statusesSelected: null,
      eventsFrom: dayjs().format("YYYY-MM-DD"),
      eventsUntil: dayjs().format("YYYY") + "-12-31",
      isHighlighted: null,
      walletStatuses: null,
      useEquipe: null,
      search: "",
      cupIds: [],
      countryIds: [],
      findEventOptions: [],
      hasEntriesOptions: [],
      findQuicklyOptions: [],
      noPeopleAssignedInRoleIds: [],
      personHavingRoleInEventId: null
    });

    watch(
      () => filters.value.eventsDatePeriod,
      v => {
        switch (v) {
          case "currentSeason":
            filters.value.eventsFrom = dayjs()
              .startOf("year")
              .format("YYYY-MM-DD");
            filters.value.eventsUntil = dayjs()
              .endOf("year")
              .format("YYYY-MM-DD");
            break;
          case "last2Weeks":
            filters.value.eventsFrom = dayjs()
              .subtract(2, "weeks")
              .startOf("month")
              .format("YYYY-MM-DD");
            filters.value.eventsUntil = dayjs()
              .endOf("year")
              .format("YYYY-MM-DD");
            break;
          case "last2Months":
            filters.value.eventsFrom = dayjs()
              .subtract(2, "months")
              .startOf("month")
              .format("YYYY-MM-DD");
            filters.value.eventsUntil = dayjs()
              .endOf("year")
              .format("YYYY-MM-DD");
            break;
          case "nextSeason":
            filters.value.eventsFrom = dayjs()
              .add(1, "year")
              .startOf("year")
              .format("YYYY-MM-DD");
            filters.value.eventsUntil = dayjs()
              .add(1, "year")
              .endOf("year")
              .format("YYYY-MM-DD");
            break;
        }
      }
    );

    const queryVariables = computed(() => {
      const _filters = filters.value;
      const statusesSelected = _filters.statusesSelected ?? [];
      if (_filters.findQuicklyOptions?.includes("schedulePublished")) {
        statusesSelected.push("registration_open");
      }
      if (_filters.findQuicklyOptions?.includes("resultsPublished")) {
        statusesSelected.push("results_published");
      }

      if (_filters.personHavingRoleInEventId) {
        _filters.personRoleFilter = {
          validCurrently: true,
          personIds: [_filters.personHavingRoleInEventId]
        };
      } else {
        _filters.personRoleFilter = undefined;
      }

      return {
        eventCategoryIds: _filters.eventCategoryIds,
        offset: 0,
        limit: 45,
        sortBy: "startDate",
        sortDesc: false,
        keyword: _filters.search?.length >= 3 ? _filters.search : null,
        disciplineIds: _filters.disciplinesSelected,
        competitionLevelIds: _filters.competitionLevelsSelected,
        regionIds: _filters.regionsSelected,
        place: _filters.eventPlaceFilterSelected,
        statuses: statusesSelected,
        organiserIds: _filters.organisersSelected,
        endDateFrom: _filters.eventsFrom,
        registrationEndDateUntil: _filters.eventsUntil,
        cupIds: _filters.cupIds,
        walletStatuses: _filters.walletStatuses,
        useEquipe: _filters.useEquipe,
        noncompetition: props.showNoncompetitions,
        profQualificationIds: _filters.profQualificationIds,
        countryIds: _filters.countryIds,

        isHighlighted:
          _filters.findEventOptions?.includes("isHighlighted") || null,
        hasTeamCompetitions:
          _filters.findEventOptions?.includes("hasTeamCompetitions") || null,
        hasChampionshipCompetitions:
          _filters.findEventOptions?.includes("hasChampionshipCompetitions") ||
          null,

        hasEntries: _filters.hasEntriesOptions?.includes("hasEntries") || null,
        hasAcceptedEntries:
          _filters.hasEntriesOptions?.includes("hasAcceptedEntries") || null,
        hasCandidacyEntries:
          _filters.hasEntriesOptions?.includes("hasCandidacyEntries") || null,

        closingSoon:
          _filters.findQuicklyOptions?.includes("closingSoon") || null,
        recentlyOpened:
          _filters.findQuicklyOptions?.includes("recentlyOpened") || null,
        noPeopleAssignedInRoleIds: _filters.noPeopleAssignedInRoleIds,
        personRoleFilter: _filters.personRoleFilter
      };
    });

    provide("calendarFilters", filters);
    provide("calendarQueryVariables", queryVariables);

    return {
      embeddedMode,
      ...getFederationConfiguration(),
      ...useCompetitionLevelHelper(),
      scrollIntoElement,
      masterEventCategoriesActive,
      getDisciplineCodenamesMapToId,
      getRegionNameShortMapToId,

      disableEventsQuery,
      filters,
      queryVariables
    };
  },
  data() {
    return {
      initEventLoad: true,
      inactiveComponent: false,
      events: [],
      hasMoreResults: true,
      // filters selected
      filtersCheck: true
    };
  },
  computed: {
    eventLoading() {
      return this.$apollo.queries.events.loading;
    }
  },
  created() {
    if (!this.$federation.isBelgiumEquestrian) {
      this.filters.countryIds = [this.masterConfiguration.countryId];
    }
  },
  mounted() {
    if (this.filterParams) {
      this.filters = Object.assign(this.filters, this.filterParams);
      if (this.filters.competitionLevelsOnlyCategoryCompetition) {
        this.filters.competitionLevelsSelected =
          this.masterCompetitionLevelsActive
            .filter(x => x.category == "COMPETITION")
            .map(x => x.id);
      }
    } else {
      const params = new URLSearchParams(window.location.search);
      const embedddedFilterKeys = ["disciplineCodenames[]", "regionNames[]"];
      if (embedddedFilterKeys.some(x => params.has(x))) {
        const filters = {
          disciplinesSelected: [],
          regionsSelected: []
        };

        const disiciplineMappings = this.getDisciplineCodenamesMapToId();
        const regionMappings = this.getRegionNameShortMapToId();
        for (const [key, value] of params) {
          switch (key) {
            case "disciplineCodenames[]":
              filters["disciplinesSelected"].push(disiciplineMappings[value]);
              break;
            case "regionNames[]":
              filters["regionsSelected"].push(regionMappings[value]);
              break;
          }
        }

        this.filters = { ...this.filters, ...filters };
      } else {
        const filterName = this.showNoncompetitions
          ? "educationCalendarFilters"
          : "calendarFilters";
        const returnedFilters = findURLParamsExternal(filterName);
        if (returnedFilters) {
          this.filters = { ...this.filters, ...returnedFilters };
        }
      }
    }

    this.filtersCheck = false;
  },
  activated() {
    if (this.filterParams) {
      this.filters = Object.assign(
        {
          eventsDatePeriod: null,
          profQualificationIds: [],
          eventCategoryIds: [],
          disciplinesSelected: [],
          competitionLevelsSelected: [],
          regionsSelected: [],
          cupIds: [],
          seasonFilterSelected: null,
          eventPlaceFilterSelected: null,
          organisersSelected: null,
          statusesSelected: null,
          eventsFrom: this.$dayjs().format("YYYY-MM-DD"),
          eventsUntil: this.$dayjs().format("YYYY") + "-12-31",
          isHighlighted: null,
          walletStatuses: null,
          useEquipe: null,
          search: "",
          noncompetition: false,
          countryIds: [this.masterConfiguration.countryId],
          findEventOptions: [],
          hasEntriesOptions: [],
          findQuicklyOptions: [],
          noPeopleAssignedInRoleIds: [],
          personHavingRoleInEventId: null
        },
        this.filterParams
      );
      if (this.filters.competitionLevelsOnlyCategoryCompetition) {
        this.filters.competitionLevelsSelected =
          this.masterCompetitionLevelsActive
            .filter(x => x.category == "COMPETITION")
            .map(x => x.id);
      }
    }
    this.inactiveComponent = false;
  },
  deactivated() {
    this.inactiveComponent = true;
  },
  apollo: {
    events: {
      query: gql`
        query events (
          ${eventsQueryVarsArgs.vars}
        ) {
          events (
            ${eventsQueryVarsArgs.args}
          ) {
            id
            ${CalendarTemplate.fragments.events}
            ${CalendarTemplate.fragments.eventCard}
          }
        }
      `,
      skip() {
        return (
          this.disableEventsQuery || this.inactiveComponent || this.filtersCheck
        );
      },
      variables() {
        return this.queryVariables;
      },
      result({ loading, data }) {
        if (loading || !data) return;
        this.hasMoreResults = data.events.length >= 45;
        this.initEventLoad = false;

        this.scrollIntoElement();
      },
      fetchPolicy: "cache-and-network"
    }
  },
  methods: {
    /**
     * For embeddedMode have all links open in new tab
     */
    async attachBlankToLinks() {
      if (!this.embeddedMode) return;
      await this.$nextTick();
      document
        .querySelectorAll("a[href]:not([target='_blank'])")
        .forEach(el => {
          el.target = "_blank";
        });
    },
    /**
     * For month calendar view. It has own queries so its best to disable the
     * query here
     */
    disableQuery(v) {
      this.disableEventsQuery = v;
    },
    loadMore() {
      // Fetch more data and transform the original result
      this.$apollo.queries.events
        .fetchMore({
          variables: {
            offset: this.events.length
          }
        })
        .then(({ data: { events } }) => {
          this.hasMoreResults = events.length >= 45;
        });
    },
    async getEvents() {
      const keyMap = [
        {
          column: this.$t(
            "calendar:CsvKeys.ID.label",
            "ID",
            "Csv ID column label"
          ),
          key: "id"
        },
        {
          column: this.$t(
            "calendar:CsvKeys.referenceCode.label",
            "Reference code"
          ),
          key: "referenceCode"
        },

        {
          column: this.$t("calendar:CsvKeys.startDateYear.label", "Year"),
          useGivenMethod(event) {
            return event.startDate.split("-")[0];
          }
        },
        {
          column: this.$t("calendar:CsvKeys.startDateMonth.label", "Month"),
          useGivenMethod: event => {
            return this.$dayjs(event.startDate).format("MMMM");
          }
        },
        {
          column: this.$t(
            "calendar:CsvKeys.startDateWeek.label",
            "Week",
            "Csv start date column label"
          ),
          useGivenMethod: event => {
            return this.$dayjs(event.startDate).format("W");
          }
        },

        {
          column: this.$t(
            "calendar:CsvKeys.StartDate.label",
            "Start date",
            "Csv start date column label"
          ),
          key: "startDate"
        },
        {
          column: this.$t(
            "calendar:CsvKeys.EndDate.label",
            "End date",
            "Csv end date column label"
          ),
          key: "endDate"
        },
        {
          column: this.$t(
            "calendar:CsvKeys.RegistrationEndDate.label",
            "Registration end date",
            "Csv registration end date column label"
          ),
          key: "registrationEndDate"
        },
        {
          column: this.$t(
            "calendar:CsvKeys.Country.label",
            "Country",
            "Csv country column label"
          ),
          key: "country.ioc"
        },
        {
          column: this.$t(
            "calendar:CsvKeys.Region.label",
            "Region",
            "Csv region column label"
          ),
          key: "region.name"
        },
        {
          column: this.$t(
            "calendar:CsvKeys.place.label",
            "Place",
            "Csv place column label"
          ),
          key: "place"
        },
        {
          column: this.$t(
            "calendar:CsvKeys.Name.label",
            "Name",
            "Csv name column label"
          ),
          key: "name"
        },
        {
          column: this.$t(
            "calendar:CsvKeys.Organiser.label",
            "Organiser",
            "Csv organiser column label"
          ),
          useGivenMethod(event) {
            return [event.organiser?.name, event.hostOrganisation?.name]
              .filter(Boolean)
              .join(", ");
          }
        },
        {
          column: this.$t(
            "calendar:CsvKeys.Discipline.label",
            "Discipline",
            "Csv discipline column label"
          ),
          key: "discipline.name",
          showCondition: () => this.showNoncompetitions === false
        },
        // Competition levels
        {
          column: this.$t(
            "calendar:CsvKeys.CompetitionLevel.label",
            "Competition Level",
            "Csv competition levels column label"
          ),
          key: "competitionLevelIds",
          showCondition: () => this.showNoncompetitions === false,
          additionalProps: {
            isArray: true,
            appendToHeader: this.masterCompetitionLevelsActive.map(x => x.name),
            useGivenMethod: (competitionLevelIds, appendToHeader) => {
              return this.masterCompetitionLevelsActive.map(activeLevel => {
                return competitionLevelIds.includes(activeLevel.id) ? 1 : 0;
              });
            }
          }
        },
        {
          column: this.$t("calendar:CsvKeys.EventCategory.label", "Category"),
          key: "eventCategories",
          showCondition: () => this.showNoncompetitions === true,
          additionalProps: {
            isArray: true,
            fieldKey: "name",
            appendToHeader: this.masterEventCategoriesActive.map(x => x.name),
            useGivenMethod: (eventCategories, appendToHeader) =>
              exportHandleArrayBooleanResponses(
                eventCategories,
                "name",
                appendToHeader
              )
          }
        },
        {
          column: this.$t(
            "calendar:CsvKeys.Status.label",
            "Status",
            "Csv status column label"
          ),
          key: "status"
        },
        {
          column: this.$t(
            "calendar:CsvKeys.BroadcastLink.label",
            "Broadcast",
            "Csv broadcast link column label"
          ),
          key: "broadcastUrl"
        },
        {
          column: this.$t(
            "calendar:CsvKeys.HomepageLink.label",
            "Homepage",
            "Csv homepage link column label"
          ),
          key: "homepageUrl"
        },
        {
          column: this.$t(
            "calendar:CsvKeys.StartingListLink.label",
            "Starting list",
            "Csv starting list link column label"
          ),
          key: "startingListUrl",
          showCondition: () => this.showNoncompetitions === false
        }
      ];

      const { data } = await this.$apollo.query({
        query: gql`
          query eventsExport(
            $keyword: String
            $disciplineIds: [ID]
            $competitionLevelIds: [ID]
            $regionIds: [ID]
            $eventIds: [ID]
            $organiserIds: [ID]
            $place: String
            $statuses: [String]
            $endDateFrom: String
            $registrationEndDateUntil: String
            $sortBy: String
            $sortDesc: Boolean
            $cupIds: [ID]
            $walletStatuses: [String]
            $useEquipe: Boolean
            $noncompetition: Boolean
            $eventCategoryIds: [ID!]
            $countryIds: [ID]
            $isHighlighted: Boolean
            $hasTeamCompetitions: Boolean
            $hasChampionshipCompetitions: Boolean
            $hasCandidacyEntries: Boolean
            $closingSoon: Boolean
            $recentlyOpened: Boolean
          ) {
            events(
              offset: 0
              limit: 2000
              keyword: $keyword
              disciplineIds: $disciplineIds
              competitionLevelIds: $competitionLevelIds
              regionIds: $regionIds
              eventIds: $eventIds
              organiserIds: $organiserIds
              place: $place
              statuses: $statuses
              endDateFrom: $endDateFrom
              registrationEndDateUntil: $registrationEndDateUntil
              sortBy: $sortBy
              sortDesc: $sortDesc
              cupIds: $cupIds
              walletStatuses: $walletStatuses
              useEquipe: $useEquipe
              noncompetition: $noncompetition
              eventCategoryIds: $eventCategoryIds
              countryIds: $countryIds

              isHighlighted: $isHighlighted
              hasTeamCompetitions: $hasTeamCompetitions
              hasChampionshipCompetitions: $hasChampionshipCompetitions
              hasCandidacyEntries: $hasCandidacyEntries
              closingSoon: $closingSoon
              recentlyOpened: $recentlyOpened
            ) {
              id
              startDate
              endDate
              registrationEndDate
              referenceCode
              countryId
              country @client {
                id
                ioc
              }
              eventCategoryIds
              eventCategories @client {
                id
                name
              }
              regionId
              region @client {
                id
                name
              }
              place
              name
              organiser {
                id
                name
                membershipType
              }
              hostOrganisation {
                id
                name
              }
              disciplineId
              discipline @client {
                id
                name
              }
              competitionLevelIds
              status
              broadcastUrl
              homepageUrl
              startingListUrl
            }
          }
        `,
        variables: this.queryVariables
      });

      this.$apollo.queries.events.refetch({
        offset: 0
      });

      return {
        keyMap,
        items: data.events,
        filename: "events-export"
      };
    }
  }
};
</script>
